import {
  formatDistanceDecimal,
  getDistanceUnitSymbolText,
} from "@/components/common/settings-context/distance-unit-utils";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import { DistanceInputField } from "@/components/ui/distance-input-field";
import { selectCadLevelsInWorldCs } from "@/store/cad/cad-slice";
import {
  selectSheetCadAlignmentReference,
  selectSheetElevation,
} from "@/store/modes/sheet-cad-alignment-mode-selectors";
import {
  setSheetElevation,
  setSheetForSheetCadAlignment,
  SheetCadAlignReference,
} from "@/store/modes/sheet-cad-alignment-mode-slice";
import { RootState } from "@/store/store";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import {
  Dropdown,
  grey,
  Option,
  TextField,
  TruncatedText,
} from "@faro-lotv/flat-ui";
import {
  IElementSection,
  IElementTypeHint,
  isIElementAreaSection,
  isIElementGenericImgSheet,
  isIElementGroup,
  isIElementSectionDataSession,
  isIElementTimeseriesDataSession,
} from "@faro-lotv/ielement-types";
import {
  selectAncestor,
  selectChildDepthFirst,
  selectIElementWorldPosition,
  selectIElementWorldTransform,
} from "@faro-lotv/project-source";
import { Stack } from "@mui/material";
import { isEqual } from "es-toolkit";
import { useEffect, useMemo, useRef, useState, type JSX } from "react";
import {
  selectAllImgSheetsInTimeSeriesDataSession,
  useSheetSelectedForAlignment,
} from "../mode-data-context";

/**
 * @returns All the sheets in the section area in an Dropdown's Option array
 * @param sectionArea The section area containing the sheets to add as an option
 */
function selectSheetOptions(sectionArea: IElementSection) {
  return (state: RootState): Option[] => {
    // Get the group area from the section area
    const groupArea = selectChildDepthFirst(
      sectionArea,
      (el) => isIElementGroup(el) && el.typeHint === IElementTypeHint.area,
    )(state);

    const sectionAreaSheet = selectChildDepthFirst(
      groupArea,
      isIElementGenericImgSheet,
    )(state);

    if (!sectionAreaSheet) {
      throw new Error("Area Section sheet not found in the Group Area");
    }

    const sheetOptions: Option[] = [
      {
        // The key and the label of the dropdown entry should display the the name of the section area
        // as its the name visible to user in the UI
        key: sectionArea.name,
        value: sectionAreaSheet.id,
        label: sectionArea.name,
      },
    ];

    // Add the sheets in the time series data session
    const timeSeriesDataSession = selectChildDepthFirst(
      sectionArea,
      isIElementTimeseriesDataSession,
    )(state);

    const sheetsInTimeSeriesDataSession =
      selectAllImgSheetsInTimeSeriesDataSession(timeSeriesDataSession)(state);

    for (const currentSheet of sheetsInTimeSeriesDataSession) {
      const sheetSectionDataSessionParent = selectAncestor(
        currentSheet,
        isIElementSectionDataSession,
      )(state);
      if (!sheetSectionDataSessionParent) {
        throw new Error(
          "Section DataSession not found in ancestor of the sheet",
        );
      }
      sheetOptions.push({
        // Like for the sheets outside the time series data session, we are using here the name of a parent section as key and label
        key: sheetSectionDataSessionParent.name,
        value: currentSheet.id,
        label: sheetSectionDataSessionParent.name,
      });
    }
    return sheetOptions;
  };
}

/**
 * @returns cad alignment set elevation panel
 */
export function SheetCadAlignmentSetElevationPanel(): JSX.Element {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const sheet = useSheetSelectedForAlignment("sheetAndCad");

  const sectionArea = useAppSelector(
    selectAncestor(sheet, isIElementAreaSection),
  );

  if (!sectionArea) {
    throw new Error("Section Area not found for selected sheet.");
  }

  const alignmentReference = useAppSelector(selectSheetCadAlignmentReference);

  const sheetOptions = useAppSelector(selectSheetOptions(sectionArea), isEqual);
  const cadLevelsDescription = useAppSelector(selectCadLevelsInWorldCs);
  const cadLevelsOptions = useMemo(
    () =>
      cadLevelsDescription?.map((level) => ({
        key: level.Name,
        value: level.Elevation.toString(),
        label: level.Name,
      })),
    [cadLevelsDescription],
  );

  const sheetElevation = useAppSelector(selectSheetElevation);

  // Define the state variables for the levels dropdown
  const [levelsDropdownSelection, setLevelsDropdownSelection] = useState("");

  // variable to keep track of whether the level dropdowns has been changed by the user
  const levelsDropdownChanged = useRef(false);

  // Update the UI when the sheet elevation changes
  useEffect(() => {
    if (!levelsDropdownChanged.current) {
      setLevelsDropdownSelection("");
    }

    levelsDropdownChanged.current = false;
  }, [sheetElevation]);

  const distanceUnit = useDistanceUnit();

  const sheetPositionLabelWithUnit = useMemo(
    () => `Sheet elevation\u00A0(${getDistanceUnitSymbolText(distanceUnit)})`,
    [distanceUnit],
  );

  const sheetPosition = useAppSelector(selectIElementWorldPosition(sheet.id));
  const sheetPositionText = useMemo(
    () => formatDistanceDecimal(sheetPosition[1], distanceUnit, false),
    [distanceUnit, sheetPosition],
  );

  return (
    <Stack
      direction="column"
      spacing={3}
      sx={{
        borderLeft: "40px",
        borderLeftColor: grey[50],
        borderLeftStyle: "solid",
        borderRight: "1px",
        borderRightColor: grey[200],
        borderRightStyle: "solid",
        pl: 1,
        pr: 1,
        backgroundColor: grey[0],
        width: 350,
        zIndex: 1,
      }}
    >
      <TruncatedText
        sx={{
          pt: "15px",
          fontSize: "18px",
          fontWeight: 600,
        }}
      >
        Set Elevation
      </TruncatedText>

      <Dropdown
        label="Sheet"
        options={sheetOptions}
        disabled={sheetOptions.length === 1}
        sx={{ marginBottom: "25px", height: "35px" }}
        value={sheet.id}
        onChange={(event) => {
          const sheetId = event.target.value;
          dispatch(setSheetForSheetCadAlignment(sheetId));
          const initialSheetElevation = selectIElementWorldTransform(sheetId)(
            store.getState(),
          ).position[1];
          dispatch(setSheetElevation(initialSheetElevation));
        }}
      />

      {alignmentReference === SheetCadAlignReference.sheet && (
        <TextField
          label={sheetPositionLabelWithUnit}
          text={sheetPositionText}
          readOnly
        />
      )}

      <Dropdown
        label="Level"
        options={
          cadLevelsOptions ?? [{ key: "N/A", value: "N/A", label: "N/A" }]
        }
        disabled={!cadLevelsOptions}
        sx={{ marginBottom: "25px", height: "35px" }}
        value={cadLevelsOptions?.length ? levelsDropdownSelection : "N/A"}
        onChange={(event) => {
          levelsDropdownChanged.current = true;
          const newHeight = parseFloat(event.target.value);
          setLevelsDropdownSelection(event.target.value);
          dispatch(setSheetElevation(newHeight));
        }}
      />

      <DistanceInputField
        fullWidth
        label={
          alignmentReference === SheetCadAlignReference.bimModel
            ? "Elevation"
            : "Matching model elevation"
        }
        sx={{ height: "35px" }}
        distanceUnit={distanceUnit}
        distanceInMeter={sheetElevation}
        errorMessage="Elevation must be a number"
        onDistanceChanged={(newValue) => {
          if (newValue) dispatch(setSheetElevation(newValue));
        }}
      />
    </Stack>
  );
}
